import { useEffect, useState } from 'react';
import { haversineMi } from '../../utils/haversine';

const THINGS_TO_DO_RADIUS_MI = 50;
/** Slightly larger radius for ski resorts so regions like Pocatello ID get both nearby resorts (e.g. Pebble Creek ~16 mi, Kelly Canyon ~68 mi). */
const SKI_RESORT_RADIUS_MI = 70;

const STATIC_DATASET_CACHE = new Map();
const NEARBY_FEATURES_CACHE = new Map();
let CRIME_RISK_DATA_PROMISE = null;
let CRIME_RISK_DATA_CACHE = undefined;

function isWithinRadius({ centerLat, centerLng, pointLat, pointLng, radiusMi }) {
  const latDelta = radiusMi / 69;
  const lngDelta = radiusMi / (Math.cos((centerLat * Math.PI) / 180) * 69 || 1);
  if (Math.abs(pointLat - centerLat) > latDelta || Math.abs(pointLng - centerLng) > lngDelta) {
    return false;
  }
  return haversineMi(centerLat, centerLng, pointLat, pointLng) <= radiusMi;
}

function filterNearbyFeatures(features, centerLat, centerLng, radiusMi) {
  return features.filter((feature) =>
    isWithinRadius({
      centerLat,
      centerLng,
      pointLat: feature.lat,
      pointLng: feature.lon,
      radiusMi,
    })
  );
}

function loadStaticDataset(path) {
  const cached = STATIC_DATASET_CACHE.get(path);
  if (cached?.data !== undefined) return Promise.resolve(cached.data);
  if (cached?.promise) return cached.promise;

  const promise = fetch(path)
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      const features = Array.isArray(data?.features) ? data.features : [];
      STATIC_DATASET_CACHE.set(path, { data: features });
      return features;
    })
    .catch(() => {
      STATIC_DATASET_CACHE.set(path, { data: [] });
      return [];
    });

  STATIC_DATASET_CACHE.set(path, { promise });
  return promise;
}

function filterNearbyWithCache({ datasetKey, features, centerLat, centerLng, radiusMi }) {
  const cacheKey = `${datasetKey}:${centerLat}:${centerLng}:${radiusMi}`;
  const cached = NEARBY_FEATURES_CACHE.get(cacheKey);
  if (cached) return cached;

  const nearby = filterNearbyFeatures(features, centerLat, centerLng, radiusMi);
  NEARBY_FEATURES_CACHE.set(cacheKey, nearby);
  return nearby;
}

function loadCrimeRiskDataset(base) {
  if (CRIME_RISK_DATA_CACHE !== undefined) return Promise.resolve(CRIME_RISK_DATA_CACHE);
  if (CRIME_RISK_DATA_PROMISE) return CRIME_RISK_DATA_PROMISE;

  CRIME_RISK_DATA_PROMISE = fetch(`${base}/data/crime-risk-by-zcta.json`)
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      CRIME_RISK_DATA_CACHE = data || null;
      return CRIME_RISK_DATA_CACHE;
    })
    .catch(() => {
      CRIME_RISK_DATA_CACHE = null;
      return null;
    })
    .finally(() => {
      CRIME_RISK_DATA_PROMISE = null;
    });

  return CRIME_RISK_DATA_PROMISE;
}

/**
 * Load static US hot springs dataset and filter to the search center.
 */
export function useHotSpringsData({ showThingsToDo, osmLat, osmLng, onThingsToDoReady }) {
  const [hotSprings, setHotSprings] = useState(null);

  useEffect(() => {
    if (!showThingsToDo) {
      onThingsToDoReady(true);
      return;
    }
    if (osmLat == null || osmLng == null) return;

    onThingsToDoReady(false);
    setHotSprings(null);

    let cancelled = false;

    const base = process.env.PUBLIC_URL || '';
    const hotSpringsPath = `${base}/data/hot-springs-us.json`;

    loadStaticDataset(hotSpringsPath)
      .then((all) => {
        if (cancelled) return;
        const nearby = filterNearbyWithCache({
          datasetKey: 'hot-springs',
          features: all,
          centerLat: osmLat,
          centerLng: osmLng,
          radiusMi: THINGS_TO_DO_RADIUS_MI,
        });
        setHotSprings(nearby);
        onThingsToDoReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setHotSprings([]);
          onThingsToDoReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [showThingsToDo, osmLat, osmLng, onThingsToDoReady]);

  return hotSprings;
}

/**
 * Load hot springs and ski resorts when "Things to do" is enabled; report ready when both are done.
 * Used by MapView so a single readiness callback covers all things-to-do layers.
 */
export function useThingsToDoData({ showThingsToDo, osmLat, osmLng, onThingsToDoReady, searchKey = 0 }) {
  const [hotSprings, setHotSprings] = useState(null);
  const [skiResorts, setSkiResorts] = useState(null);

  useEffect(() => {
    if (!showThingsToDo) {
      onThingsToDoReady(true);
      setHotSprings(null);
      setSkiResorts(null);
      return;
    }
    if (osmLat == null || osmLng == null) return;

    onThingsToDoReady(false);
    setHotSprings(null);
    setSkiResorts(null);

    let cancelled = false;
    const base = process.env.PUBLIC_URL || '';

    const hotSpringsPath = `${base}/data/hot-springs-us.json`;
    const skiResortsPath = `${base}/data/ski-resorts-us.json`;

    const loadHotSprings = loadStaticDataset(hotSpringsPath)
      .then((all) => filterNearbyWithCache({
        datasetKey: 'hot-springs',
        features: all,
        centerLat: osmLat,
        centerLng: osmLng,
        radiusMi: THINGS_TO_DO_RADIUS_MI,
      }));

    const loadSkiResorts = loadStaticDataset(skiResortsPath)
      .then((all) => filterNearbyWithCache({
        datasetKey: 'ski-resorts',
        features: all,
        centerLat: osmLat,
        centerLng: osmLng,
        radiusMi: SKI_RESORT_RADIUS_MI,
      }));

    Promise.allSettled([loadHotSprings, loadSkiResorts]).then(([springsResult, resortsResult]) => {
      if (cancelled) return;

      const springs = springsResult.status === 'fulfilled' ? springsResult.value : [];
      const resorts = resortsResult.status === 'fulfilled' ? resortsResult.value : [];

      setHotSprings(springs);
      setSkiResorts(resorts);
      onThingsToDoReady(true);
    });

    return () => {
      cancelled = true;
    };
  // searchKey re-arms this effect on every new search so same-location re-searches
  // still call onThingsToDoReady after runSearchWithValues resets it to false.
  }, [showThingsToDo, osmLat, osmLng, onThingsToDoReady, searchKey]);

  return { hotSprings, skiResorts };
}

/**
 * @param {boolean} shouldLoad - When false, returns [] and does not fetch (default off for admin CSV tooling).
 */
export function useTenantPropertiesData(shouldLoad) {
  const [tenantProperties, setTenantProperties] = useState([]);

  useEffect(() => {
    if (!shouldLoad) {
      setTenantProperties([]);
      return undefined;
    }

    let cancelled = false;
    const base = process.env.PUBLIC_URL || '';
    const tenantPropertiesPath = `${base}/data/tenant-properties.json`;

    loadStaticDataset(tenantPropertiesPath)
      .then((all) => {
        if (cancelled) return;
        const normalized = all
          .map((item, index) => {
            const lat = Number(item?.lat);
            const lng = Number(item?.lng);
            const propertyUrl = String(item?.propertyUrl || '').trim();
            if (!Number.isFinite(lat) || !Number.isFinite(lng) || !propertyUrl) return null;
            return {
              id: item?.id || `tenant-${index + 1}`,
              lat,
              lng,
              propertyUrl,
              name: item?.name || 'Tenant Property',
              message: item?.message || null,
              monthlyCost: item?.monthlyCost || null,
              bedrooms: item?.bedrooms || null,
              bathrooms: item?.bathrooms || null,
            };
          })
          .filter(Boolean);
        setTenantProperties(normalized);
      })
      .catch(() => {
        if (!cancelled) setTenantProperties([]);
      });

    return () => {
      cancelled = true;
    };
  }, [shouldLoad]);

  return tenantProperties;
}

/**
 * Approximate bounding boxes per state [minLat, maxLat, minLng, maxLng].
 * Smallest states are listed first so overlapping boxes resolve to the
 * more specific state (e.g. CT match beats NY match).
 */
const STATE_BOUNDS_ORDERED = [
  ['DC', 38.79, 38.99, -77.12, -76.91],
  ['RI', 41.30, 42.02, -71.86, -71.12],
  ['DE', 38.45, 39.84, -75.79, -74.98],
  ['CT', 40.98, 42.05, -73.73, -71.79],
  ['NJ', 38.93, 41.36, -75.56, -73.90],
  ['NH', 42.70, 45.31, -72.56, -70.61],
  ['VT', 42.73, 45.02, -73.44, -71.50],
  ['MA', 41.24, 42.89, -73.51, -69.93],
  ['HI', 18.87, 28.40, -178.38, -154.81],
  ['MD', 37.91, 39.72, -79.49, -74.98],
  ['WV', 37.20, 40.64, -82.64, -77.72],
  ['SC', 32.05, 35.22, -83.35, -78.54],
  ['ME', 43.06, 47.46, -71.08, -66.95],
  ['IN', 37.77, 41.77, -88.10, -84.78],
  ['KY', 36.50, 39.15, -89.57, -81.96],
  ['TN', 34.98, 36.68, -90.31, -81.65],
  ['VA', 36.54, 39.46, -83.68, -75.24],
  ['OH', 38.40, 42.33, -84.82, -80.52],
  ['MS', 30.14, 35.01, -91.65, -88.10],
  ['AL', 30.14, 35.01, -88.47, -84.89],
  ['GA', 30.36, 35.00, -85.61, -80.84],
  ['NC', 33.84, 36.59, -84.32, -75.46],
  ['PA', 39.72, 42.27, -80.52, -74.69],
  ['NY', 40.50, 45.02, -79.76, -71.86],
  ['LA', 28.93, 33.02, -94.04, -88.82],
  ['AR', 33.00, 36.50, -94.62, -89.64],
  ['OK', 33.62, 37.00, -103.00, -94.43],
  ['IA', 40.37, 43.50, -96.64, -90.14],
  ['WI', 42.49, 47.08, -92.89, -86.25],
  ['FL', 24.40, 31.01, -87.63, -80.03],
  ['IL', 36.97, 42.51, -91.51, -87.02],
  ['MO', 35.99, 40.61, -95.77, -89.10],
  ['KS', 36.99, 40.00, -102.05, -94.59],
  ['NE', 40.00, 43.00, -104.05, -95.31],
  ['SD', 42.48, 45.94, -104.06, -96.44],
  ['ND', 45.93, 49.00, -104.05, -96.55],
  ['MN', 43.50, 49.38, -97.24, -89.49],
  ['MI', 41.70, 48.31, -90.42, -82.41],
  ['ID', 41.99, 49.00, -117.24, -111.04],
  ['OR', 41.99, 46.24, -124.57, -116.46],
  ['WY', 40.99, 45.01, -111.05, -104.05],
  ['CO', 36.99, 41.00, -109.05, -102.04],
  ['UT', 37.00, 42.00, -114.05, -109.04],
  ['NM', 31.33, 37.00, -109.05, -103.00],
  ['AZ', 31.33, 37.00, -114.82, -109.05],
  ['NV', 35.00, 42.00, -120.00, -114.04],
  ['WA', 45.54, 49.00, -124.73, -116.92],
  ['MT', 44.36, 49.00, -116.05, -104.04],
  ['CA', 32.53, 42.01, -124.41, -114.13],
  ['TX', 25.84, 36.50, -106.65, -93.51],
  ['AK', 51.21, 71.40, -179.15, -129.98],
  ['PR', 17.88, 18.53, -67.27, -65.22],
];

function stateFromLatLng(lat, lng) {
  for (const [state, minLat, maxLat, minLng, maxLng] of STATE_BOUNDS_ORDERED) {
    if (lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng) return state;
  }
  return null;
}

const GEOJSON_BY_STATE_CACHE = new Map();

/**
 * Load ZCTA score + boundary data needed for the crime overlay.
 * Accepts workCenter/homeCenter to load only the relevant state's GeoJSON
 * (~1–3 MB) instead of the full nationwide file (70 MB).
 */
export function useZctaOverlayData({ zctaCrimeDataProp, workCenter, homeCenter }) {
  const [localZctaData, setLocalZctaData] = useState(null);
  const [fullGeoJson, setFullGeoJson] = useState(null);

  useEffect(() => {
    if (zctaCrimeDataProp != null) return;

    let cancelled = false;
    const base = process.env.PUBLIC_URL || '';
    loadCrimeRiskDataset(base)
      .then((data) => {
        if (!cancelled) setLocalZctaData(data || null);
      })
      .catch(() => {
        if (!cancelled) setLocalZctaData(null);
      });

    return () => {
      cancelled = true;
    };
  }, [zctaCrimeDataProp]);

  // Destructure coords so the effect only re-runs when the actual values change
  const [workLat, workLng] = workCenter ?? [];
  const [homeLat, homeLng] = homeCenter ?? [];

  useEffect(() => {
    // Pick work center first, fall back to home center
    const lat = workLat ?? homeLat;
    const lng = workLng ?? homeLng;
    if (lat == null || lng == null) return;

    const state = stateFromLatLng(lat, lng);
    if (!state) {
      console.warn('useZctaOverlayData: could not determine state from coordinates');
      return;
    }

    const cached = GEOJSON_BY_STATE_CACHE.get(state);
    if (cached) {
      setFullGeoJson(cached);
      return undefined;
    }

    const controller = new AbortController();
    const base = process.env.PUBLIC_URL || '';
    fetch(`${base}/data/zcta-by-state/${state}.geojson`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const resolved = data || null;
        if (resolved) GEOJSON_BY_STATE_CACHE.set(state, resolved);
        setFullGeoJson(resolved);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        console.error('Failed to load ZCTA GeoJSON:', err);
        setFullGeoJson(null);
      });

    return () => {
      controller.abort();
    };
  }, [workLat, workLng, homeLat, homeLng]);

  return {
    zctaData: zctaCrimeDataProp != null ? zctaCrimeDataProp : localZctaData,
    fullGeoJson,
  };
}
